// The confirm button's behaviour per draft (v3 `useSetupDrafts.ts`, KUR-1). A press is sent at most
// once from here; the backend's own single-use claim catches anything else (a second tab).
import { orpheusApi, type OrpheusApi } from '../api/OrpheusApi'
import type { WorkspaceId } from '../models/orpheus.types'
import { orpheusConsentRequired } from '../service/orpheusConsent'
import { draftConfirmFailure, spentDraftFailure } from '../service/orpheusDraftConfirm'
import { useOrpheusConsentStore } from '../stores/orpheusConsentStore'
import { useOrpheusSetupDraftStore, type DraftState } from '../stores/orpheusSetupDraftStore'

import { useCreditGuard } from '@/features/credits'

export type { DraftState } from '../stores/orpheusSetupDraftStore'

// Refused before the backend claimed the draft, so the same draft can still be saved once the
// cause is gone. Any other failure spent it (a draft is single-use).
const RETRYABLE_FAILURES = ['generic', 'consent', 'permission']

type SavedListener = (draftRef: string, result: Record<string, unknown> | null) => void
const savedListeners = new Set<SavedListener>()

/** Told whenever a draft is saved, anywhere — a host refreshes what the draft created. */
export function onDraftSaved(listener: SavedListener): () => void {
  savedListeners.add(listener)
  return () => {
    savedListeners.delete(listener)
  }
}

export function canRetryDraft(state: DraftState | null): boolean {
  return state?.status === 'failed' && RETRYABLE_FAILURES.includes(state.failure?.kind ?? '')
}

/**
 * What a reloaded card's draft became, from the status the history stamps on it, so a saved or
 * replaced card doesn't offer a button that can only answer 409.
 */
export function storedDraftState(draftStatus: string | null | undefined): DraftState | null {
  if (draftStatus === 'applied') return { status: 'saved', result: null, failure: null }
  if (draftStatus === 'applying') return { status: 'confirming', result: null, failure: null }
  if (draftStatus === 'superseded' || draftStatus === 'failed') {
    return { status: 'failed', result: null, failure: spentDraftFailure(draftStatus) }
  }
  return null
}

export function useSetupDrafts(api: Pick<OrpheusApi, 'confirmDraft'> = orpheusApi) {
  const store = useOrpheusSetupDraftStore()
  const consent = useOrpheusConsentStore()
  const credits = useCreditGuard()

  function draftState(draftRef: string | null | undefined): DraftState | null {
    return store.get(draftRef)
  }

  async function confirmDraft(workspaceId: WorkspaceId, draftRef: string): Promise<DraftState> {
    const existing = store.get(draftRef)
    if (existing && !canRetryDraft(existing)) return existing

    store.set(draftRef, { status: 'confirming', result: null, failure: null })
    try {
      const { result } = await api.confirmDraft(workspaceId, draftRef)
      const saved: DraftState = { status: 'saved', result: result ?? null, failure: null }
      store.set(draftRef, saved)
      savedListeners.forEach((listener) => listener(draftRef, saved.result))
      // A labelling or Impact Analysis run spends credits when it starts.
      void credits.refreshBalance().catch(() => undefined)
    } catch (cause) {
      const failure = draftConfirmFailure(cause)
      const required = orpheusConsentRequired(cause)
      if (required) consent.markConsentRequired(required.accountAdmins)
      store.set(draftRef, {
        status: failure.kind === 'already_saved' ? 'saved' : 'failed',
        result: null,
        failure,
      })
    }
    return store.get(draftRef) ?? { status: 'failed', result: null, failure: null }
  }

  // Discard only closes the card on this screen: nothing was saved, so nothing is undone.
  function discardDraft(draftRef: string): void {
    if (store.get(draftRef)?.status === 'saved') return
    store.set(draftRef, { status: 'discarded', result: null, failure: null })
  }

  return { draftState, confirmDraft, discardDraft }
}
